import { useState } from "react";

import { Button } from "../../ui/Button";
import { Select } from "../../ui/Select";
import { cn } from "../../ui/cn";
import { ColorPicker } from "./ColorPicker";
import {
  PALETTES,
  PALETTE_LABELS,
  seriesColor,
  type PaletteId,
} from "./palette";

/**
 * A card's colours: a scheme for the whole thing, and an override per series.
 *
 * Two levels because there are two kinds of ask. "Make this readable for
 * somebody who is colourblind" is about the whole card and is a preset;
 * "our brand red is exactly this" is about one series and is a hex. Offering
 * only the second would mean setting nine colours by hand to get an accessible
 * chart; offering only the first would mean never matching a brand guide.
 *
 * An override is a deliberate pin, so it survives changing the preset and has
 * its own reset. Somebody who set Support to their brand colour and then tried
 * the pastel scheme should get pastel everything *except* Support.
 */
export function ColorPanel({
  series,
  palette,
  overrides,
  dark,
  onPaletteChange,
  onColorChange,
}: {
  /** Series keys in the card's own order, with the colour actually drawn. */
  series: readonly { key: string; label: string; color: string }[];
  palette: PaletteId;
  overrides: Record<string, string>;
  dark: boolean;
  onPaletteChange: (palette: PaletteId) => void;
  /** Null clears the pin and hands the series back to the palette. */
  onColorChange: (key: string, color: string | null) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const pinned = Object.keys(overrides).length;

  return (
    <div className="flex w-72 flex-col gap-2 p-2">
      <div className="flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide text-faint">
          Scheme
        </span>
        <Select
          value={palette}
          onChange={onPaletteChange}
          label="Colour scheme"
          className="ml-auto"
          options={PALETTES.map((id) => ({ value: id, label: PALETTE_LABELS[id] }))}
        />
      </div>

      {/* What the scheme looks like before committing to it, which is quicker
          than switching and reading the chart each time. */}
      <div className="flex gap-1">
        {Array.from({ length: Math.max(series.length, 5) }, (_, index) => (
          <span
            key={index}
            aria-hidden
            className="h-4 flex-1 rounded-sm"
            style={{
              backgroundColor: seriesColor(
                index,
                palette,
                dark,
                Math.max(series.length, 5),
              ),
            }}
          />
        ))}
      </div>

      <div className="border-t border-line pt-1">
        <div className="flex items-center gap-2 pb-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Series
          </span>
          {pinned > 0 ? (
            <button
              type="button"
              onClick={() => {
                for (const key of Object.keys(overrides)) onColorChange(key, null);
              }}
              className="ml-auto text-[11px] text-brand-600 hover:text-brand-700"
            >
              Reset {pinned}
            </button>
          ) : null}
        </div>

        <ul className="max-h-64 space-y-0.5 overflow-y-auto">
          {series.map((entry) => (
            <li key={entry.key}>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setEditing(editing === entry.key ? null : entry.key)
                  }
                  aria-expanded={editing === entry.key}
                  className={cn(
                    "flex min-w-0 flex-1 items-center gap-2 rounded px-1 py-1 text-left text-xs",
                    "hover:bg-surface-2",
                    editing === entry.key && "bg-surface-2",
                  )}
                >
                  <span
                    aria-hidden
                    className="size-3.5 shrink-0 rounded border border-line"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="min-w-0 flex-1 truncate text-content">
                    {entry.label}
                  </span>
                  {overrides[entry.key] ? (
                    <span
                      title="Pinned — this one keeps its colour when the scheme changes"
                      className="shrink-0 text-[10px] uppercase text-faint"
                    >
                      pinned
                    </span>
                  ) : null}
                </button>

                {overrides[entry.key] ? (
                  <button
                    type="button"
                    onClick={() => onColorChange(entry.key, null)}
                    aria-label={`Reset ${entry.label} to the scheme`}
                    title="Back to the scheme's colour"
                    className="shrink-0 text-[11px] text-faint hover:text-brand-600 dark:hover:text-brand-400"
                  >
                    ✕
                  </button>
                ) : null}
              </div>

              {editing === entry.key ? (
                <div className="px-1 pb-2 pt-1">
                  <ColorPicker
                    value={entry.color}
                    onChange={(hex) => onColorChange(entry.key, hex)}
                  />
                </div>
              ) : null}
            </li>
          ))}
        </ul>

        {series.length === 0 ? (
          <p className="px-1 py-2 text-[11px] text-faint">
            Nothing drawn yet. Colours appear here once this card has data.
          </p>
        ) : null}
      </div>

      <div className="flex justify-end border-t border-line pt-2">
        <Button
          size="sm"
          variant="ghost"
          disabled={pinned === 0 && palette === "brand"}
          onClick={() => {
            onPaletteChange("brand");
            for (const key of Object.keys(overrides)) onColorChange(key, null);
          }}
        >
          Back to defaults
        </Button>
      </div>
    </div>
  );
}
